import React, { FunctionComponent } from 'react';
import { faCopy, faBan, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faPen } from '@fortawesome/pro-regular-svg-icons';
import { faLocationDot } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import PetTypeTags from 'components/UI/V3/PetTypeTags';
import ThreeDotsDropdown from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdown';
import { Service, isTrainingService, SESSION_BOOKING_TYPES } from 'model/Service';
import styles from './TrainingServiceRow.module.scss';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';

interface TrainingServiceRowProps {
    service: Service;
    isLast: boolean;
    isMobile: boolean;
    onEdit?: (service: Service) => void;
    onDuplicate?: (service: Service) => void;
    onDeactivate?: (service: Service) => void;
    onDelete?: (service: Service) => void;
}

const TrainingServiceRow: FunctionComponent<TrainingServiceRowProps> = ({
    service,
    isLast,
    isMobile,
    onEdit,
    onDuplicate,
    onDeactivate,
    onDelete
}) => {
    const { getOrganizationMarketplace } = useOrganizationMarketplaces();

    const displayPrice = service.pricing.fixedPrice[0]?.price || 0;
    const petTypes = service.petTypes;
    const marketplaceId = service.marketplaceId ?? service.marketplaces[0]?.marketplaceId;
    const marketplaceName = marketplaceId != null ? getOrganizationMarketplace(marketplaceId)?.name : undefined;

    const isActive = service.status.id === 1;
    const staffCount = service.staff?.length || 0;
    const staffDisplay = staffCount === 1 ? '1 Member' : `${staffCount} Members`;

    const getBookingTypeLabel = () => {
        if (!isTrainingService(service)) return '-';
        return service.trainingData.bookingType === SESSION_BOOKING_TYPES.PRIVATE_SESSION
            ? 'Private Session'
            : 'Group Session';
    };

    const formatPrice = (price: number) => {
        return `$${price.toFixed(2)}`;
    };

    const getTaxesDisplay = (taxes: any[]) => {
        if (taxes.length === 0) {
            return 'No Taxable';
        }
        return taxes.length === 1 ? `${taxes.length} tax` : `${taxes.length} taxes`;
    };

    const editOption = {
        id: 'edit',
        label: 'Edit',
        icon: faPen,
        onClick: () => onEdit?.(service)
    };

    const duplicateOption = {
        id: 'duplicate',
        label: 'Duplicate',
        icon: faCopy,
        onClick: () => onDuplicate?.(service)
    };

    const deactivateOption = {
        id: 'deactivate',
        label: isActive ? 'Deactivate' : 'Activate',
        icon: faBan,
        onClick: () => onDeactivate?.(service)
    };

    const options = [editOption, duplicateOption, deactivateOption];

    const petTypesAndLocation = (
        <div className={styles.petAndLocationContainer}>
            <PetTypeTags petTypes={petTypes} />
            {marketplaceName && (
                <div className={styles.marketplaceContainer}>
                    <FontAwesomeIcon
                        icon={faLocationDot}
                        className={styles.marketplaceIcon}
                    />
                    <span className={styles.marketplaceName}>{marketplaceName}</span>
                </div>
            )}
        </div>
    );

    if (isMobile) {
        return (
            <TableRow className={styles.mobileRow}>
                <TableCell className={styles.nameCell}>
                    <div
                        className={styles.serviceColor}
                        style={{ backgroundColor: service.color || 'transparent' }}
                    ></div>
                    <div className={styles.nameCell}>
                        <p className={styles.serviceName}>{service.name}</p>
                        {petTypesAndLocation}
                    </div>
                </TableCell>

                <TableCell isActions>
                    <ThreeDotsDropdown
                        options={options}
                        direction={isLast ? 'up' : 'down'}
                    />
                </TableCell>
            </TableRow>
        );
    }

    return (
        <TableRow className={styles.desktopRow}>
            <TableCell className={styles.nameMainCell}>
                <div
                    className={styles.serviceColor}
                    style={{ backgroundColor: service.color || 'transparent' }}
                ></div>
                <div className={styles.nameCell}>
                    <p className={styles.serviceName}>{service.name}</p>
                    {petTypesAndLocation}
                </div>
            </TableCell>

            <TableCell>
                <p>{staffDisplay}</p>
            </TableCell>

            <TableCell>
                <p>{getBookingTypeLabel()}</p>
            </TableCell>

            <TableCell>
                <p className={styles.price}>{formatPrice(displayPrice)}</p>
            </TableCell>

            <TableCell>
                <p>{getTaxesDisplay(service.pricing.taxes)}</p>
            </TableCell>

            <TableCell isActions>
                <ThreeDotsDropdown
                    options={options}
                    direction={isLast ? 'up' : 'down'}
                />
            </TableCell>
        </TableRow>
    );
};

export default TrainingServiceRow;
